import { Box } from '@mui/material';
import { useEffect, useState } from 'react';

import { COLORS } from '@/styles/tokens/colors';

/** The default side length, in pixels, of the square thumbnail frame. */
const DEFAULT_THUMBNAIL_SIZE = 96;

/**
 * The image to show: a stored Image, by its ID, or a local file that is not
 * stored yet, such as an attachment of a message that is still sending.
 */
export type ImageThumbnailSource = { imageId: string } | { file: File };

export interface ImageThumbnailProps {
  source: ImageThumbnailSource;
  alt: string;
  /** The side length, in pixels, of the square frame. */
  size?: number;
}

/**
 * Returns the URL of the server route that streams a stored image. The route
 * authenticates with the session cookie, so the URL works as an <img> source.
 */
function getStoredImageUrl(imageId: string) {
  return `/api/images/${encodeURIComponent(imageId)}`;
}

/**
 * Returns an object URL for file, or undefined when there is no file. The
 * URL is released when the file changes or the caller unmounts.
 */
function useObjectUrl(file: File | undefined) {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!file) {
      setUrl(undefined);
      return;
    }
    const nextUrl = URL.createObjectURL(file);
    setUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);
  return url;
}

/**
 * A square thumbnail of a stored image or a local image file. The fixed frame
 * keeps the surrounding layout stable while the image loads. The whole image
 * fits in the frame without cropping, so a diagram shows its full layout.
 */
export function ImageThumbnail({
  source,
  alt,
  size = DEFAULT_THUMBNAIL_SIZE,
}: ImageThumbnailProps) {
  const objectUrl = useObjectUrl('file' in source ? source.file : undefined);
  const src =
    'imageId' in source ? getStoredImageUrl(source.imageId) : objectUrl;
  return (
    <Box
      component="img"
      data-testid="image-thumbnail"
      src={src}
      alt={alt}
      sx={{
        width: size,
        height: size,
        objectFit: 'contain',
        bgcolor: COLORS.PRIMITIVES.WHITE,
        border: `1px solid ${COLORS.GRAY[300]}`,
        borderRadius: '6px',
      }}
    />
  );
}
